/* Shanium Yum — Core theme JS
   Handles scroll reveal, header behaviour, mobile drawer,
   mega menu, accordions, sticky ATC, video popup, gallery,
   variant selection, and dispatches app-block friendly events.
*/
(function () {
  'use strict';

  /* Prevent mobile reload jumping to carousels lower on the page */
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }
  const resetPageScroll = () => {
    if (window.location.hash) return;
    const goTop = () => {
      if (window.scrollY > 0) window.scrollTo(0, 0);
    };
    goTop();
    requestAnimationFrame(goTop);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', resetPageScroll, { once: true });
  } else {
    resetPageScroll();
  }
  window.addEventListener('load', resetPageScroll, { once: true });
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) resetPageScroll();
  });

  /* Header scroll state ----------------------------------- */
  const header = document.querySelector('[data-header]');
  const announcement = document.querySelector('[data-announcement-bar]');
  const setHeaderHeight = () => {
    if (!header) return;
    document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
  };
  const setAnnouncementStickyHeight = () => {
    const stickyBar = document.querySelector('[data-announcement-sticky]');
    const height = stickyBar ? stickyBar.offsetHeight : 0;
    document.documentElement.style.setProperty('--announcement-sticky-height', `${height}px`);
  };
  const onScroll = () => {
    if (!header) return;
    header.classList.toggle('site-header--scrolled', window.scrollY > 24);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    setHeaderHeight();
    setAnnouncementStickyHeight();
  }, { passive: true });
  setHeaderHeight();
  setAnnouncementStickyHeight();
  onScroll();
  if (announcement && 'ResizeObserver' in window) {
    new ResizeObserver(setAnnouncementStickyHeight).observe(announcement);
  }

  /* Category grid: even = full rows, odd = n x (n-1), last row centered */
  const categoryColumnCount = (count) => {
    const max = 4;
    if (count <= 1) return 1;
    if (count <= 4) return count;
    if (count % 2 === 1) return Math.min(max, Math.ceil(count / 2));
    if (count % 4 === 0) return 4;
    if (count % 3 === 0) return 3;
    return 4;
  };
  const layoutCategoryGrids = () => {
    document.querySelectorAll('[data-category-grid]').forEach((grid) => {
      const count = grid.querySelectorAll('.category-card').length;
      grid.style.setProperty('--category-cols', String(categoryColumnCount(count)));
    });
  };
  layoutCategoryGrids();

  /* Ambient videos — muted loop, always playing (expandable viewers get audio separately) */
  const isExpandableVideoEl = (video) => {
    if (!video) return false;
    if (video.matches('[data-video-expanded], [data-reel-modal-video]')) return true;
    return !!video.closest('[data-video-popup], [data-reel-modal]');
  };

  const isAmbientVideoEl = (video) => {
    if (!video || isExpandableVideoEl(video)) return false;
    if (video.closest('[hidden]')) return false;
    const heroSlide = video.closest('[data-hero-slide]');
    if (heroSlide && !heroSlide.classList.contains('is-active')) return false;
    return true;
  };

  const playAmbientVideo = (video) => {
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.autoplay = true;
    const onPlaying = () => {
      video.removeAttribute('poster');
    };
    video.addEventListener('playing', onPlaying, { once: true });
    if (video.paused) {
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
    }
  };

  const scheduleHeroVideoBoot = () => {
    bootAmbientVideos();
    [150, 450, 1000, 2200].forEach((ms) => {
      window.setTimeout(bootAmbientVideos, ms);
    });
  };

  const playHeroSlideVideo = (slide) => {
    if (!slide || !slide.classList.contains('is-active')) return;
    const video = slide.querySelector('[data-hero-video], video');
    if (!video) return;
    if (video.readyState < 2) video.load();
    playAmbientVideo(video);
  };

  const bootHeroVideos = () => {
    if (document.hidden) return;
    document.querySelectorAll('.hero').forEach((hero) => {
      const active = hero.querySelector('[data-hero-slide].is-active');
      if (active) playHeroSlideVideo(active);
      else {
        hero.querySelectorAll('[data-hero-slide] video').forEach((video) => playAmbientVideo(video));
      }
    });
  };

  const bootAmbientVideos = () => {
    if (document.hidden) return;
    bootHeroVideos();
    document.querySelectorAll('video').forEach((video) => {
      if (isAmbientVideoEl(video)) playAmbientVideo(video);
    });
  };

  document.querySelectorAll('.hero [data-hero-video], .hero video').forEach((video) => {
    const retry = () => {
      if (isAmbientVideoEl(video)) playAmbientVideo(video);
    };
    video.addEventListener('loadeddata', retry, { once: true });
    video.addEventListener('canplay', retry, { once: true });
    video.addEventListener('canplaythrough', retry, { once: true });
  });

  if ('IntersectionObserver' in window) {
    const heroVideoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const slide = entry.target.closest('[data-hero-slide]');
          if (slide?.classList.contains('is-active')) playHeroSlideVideo(slide);
        });
      },
      { threshold: 0.12, rootMargin: '40px 0px' }
    );
    document.querySelectorAll('.hero [data-hero-video]').forEach((video) => heroVideoObserver.observe(video));
  }

  scheduleHeroVideoBoot();
  window.addEventListener('load', scheduleHeroVideoBoot);
  window.addEventListener('pageshow', scheduleHeroVideoBoot);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) scheduleHeroVideoBoot();
  });

  /* Hero media rotator ------------------------------------ */
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.querySelectorAll('[data-hero-slider]').forEach((hero) => {
    const slides = Array.from(hero.querySelectorAll('[data-hero-slide]'));
    const dots = Array.from(hero.querySelectorAll('[data-hero-dot]'));
    if (slides.length < 2) return;

    let index = 0;
    let timer = null;
    const interval = parseInt(hero.getAttribute('data-hero-interval') || '5000', 10);

    const setActive = (next) => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        const on = i === index;
        slide.classList.toggle('is-active', on);
        const video = slide.querySelector('[data-hero-video], video');
        if (!video) return;
        if (on) {
          playHeroSlideVideo(slide);
        } else {
          video.pause();
          try {
            video.currentTime = 0;
          } catch (e) {
            /* ignore seek errors */
          }
        }
      });
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };

    const start = () => {
      stop();
      if (reduceMotion || document.hidden) return;
      timer = window.setInterval(() => setActive(index + 1), interval);
    };
    const stop = () => {
      if (timer) window.clearInterval(timer);
      timer = null;
    };

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        setActive(i);
        start();
      });
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stop();
        slides.forEach((slide) => slide.querySelector('video')?.pause());
      } else {
        setActive(index);
        start();
      }
    });

    setActive(0);
    start();
  });

  /* Testimonials slider ----------------------------------- */
  document.querySelectorAll('[data-testimonials-slider]').forEach((root) => {
    const track = root.querySelector('[data-testimonials-track]');
    const slides = Array.from(root.querySelectorAll('[data-testimonials-slide]'));
    const dots = Array.from(root.querySelectorAll('[data-testimonials-dot]'));
    const prev = root.querySelector('[data-testimonials-prev]');
    const next = root.querySelector('[data-testimonials-next]');
    if (!track || slides.length < 2) return;

    let index = 0;
    let timer = null;
    let startX = 0;
    const interval = Math.max(3, parseInt(root.getAttribute('data-rotate-seconds') || '5', 10)) * 1000;
    const isCards = root.classList.contains('testimonials-slider--cards');

    const getPerView = () => {
      if (!isCards) return 1;
      if (window.matchMedia('(min-width: 990px)').matches) return 3;
      if (window.matchMedia('(min-width: 750px)').matches) return 2;
      return 1;
    };

    const maxIndex = () => Math.max(0, slides.length - getPerView());

    const go = (nextIndex) => {
      const max = maxIndex();
      if (nextIndex < 0) index = max;
      else if (nextIndex > max) index = 0;
      else index = nextIndex;

      if (isCards) {
        const slide = slides[0];
        const styles = window.getComputedStyle(track);
        const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        const step = slide.getBoundingClientRect().width + gap;
        track.style.transform = `translateX(-${index * step}px)`;
      } else {
        track.style.transform = `translateX(-${index * 100}%)`;
      }

      slides.forEach((slide, i) => {
        const on = i >= index && i < index + getPerView();
        slide.classList.toggle('is-active', on);
        slide.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      dots.forEach((dot, i) => {
        const on = i === index;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    };

    const start = () => {
      stop();
      if (reduceMotion || document.hidden || maxIndex() < 1) return;
      timer = window.setInterval(() => go(index + 1), interval);
    };
    const stop = () => {
      if (timer) window.clearInterval(timer);
      timer = null;
    };

    prev?.addEventListener('click', () => { go(index - 1); start(); });
    next?.addEventListener('click', () => { go(index + 1); start(); });
    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => { go(i); start(); });
    });

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('pointerdown', (e) => { startX = e.clientX; });
    root.addEventListener('pointerup', (e) => {
      const delta = e.clientX - startX;
      if (Math.abs(delta) < 40) return;
      go(index + (delta < 0 ? 1 : -1));
      start();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else start();
    });
    window.addEventListener('resize', () => go(Math.min(index, maxIndex())), { passive: true });

    go(0);
    start();
  });

  /* Product recommendations slider (You might also like) -- */
  document.querySelectorAll('[data-product-recs-slider]').forEach((root) => {
    const track = root.querySelector('[data-product-recs-track]');
    const slides = Array.from(root.querySelectorAll('[data-product-recs-slide]'));
    const prev = root.querySelector('[data-product-recs-prev]');
    const next = root.querySelector('[data-product-recs-next]');
    if (!track || !slides.length) return;

    let index = 0;
    let startX = 0;

    const getPerView = () => {
      if (window.matchMedia('(min-width: 990px)').matches) return 4;
      if (window.matchMedia('(min-width: 750px)').matches) return 2;
      return 1;
    };

    const maxIndex = () => Math.max(0, slides.length - getPerView());

    const go = (nextIndex) => {
      const max = maxIndex();
      if (max < 1) {
        index = 0;
        track.style.transform = 'translateX(0)';
        slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i < getPerView());
          slide.setAttribute('aria-hidden', i < getPerView() ? 'false' : 'true');
        });
        return;
      }
      if (nextIndex < 0) index = max;
      else if (nextIndex > max) index = 0;
      else index = nextIndex;

      const slide = slides[0];
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      const step = slide.getBoundingClientRect().width + gap;
      track.style.transform = `translateX(-${index * step}px)`;

      slides.forEach((slideEl, i) => {
        const on = i >= index && i < index + getPerView();
        slideEl.classList.toggle('is-active', on);
        slideEl.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
    };

    prev?.addEventListener('click', () => go(index - 1));
    next?.addEventListener('click', () => go(index + 1));

    root.addEventListener('pointerdown', (e) => { startX = e.clientX; });
    root.addEventListener('pointerup', (e) => {
      const delta = e.clientX - startX;
      if (Math.abs(delta) < 40) return;
      go(index + (delta < 0 ? 1 : -1));
    });

    window.addEventListener('resize', () => go(Math.min(index, maxIndex())), { passive: true });
    go(0);
  });

  /* Reveal on scroll -------------------------------------- */
  const io = ('IntersectionObserver' in window)
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -60px 0px' })
    : null;
  document.querySelectorAll('[data-reveal]').forEach((el) => {
    if (io) io.observe(el); else el.classList.add('is-in');
  });

  /* Mobile drawer + overlay ------------------------------- */
  const overlay = document.querySelector('[data-overlay]');
  function setDrawerChrome(open) {
    document.documentElement.classList.toggle('drawer-open', open);
    if (overlay) overlay.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.body.style.overflow = open ? 'hidden' : '';
  }
  function toggleDrawer(id, force) {
    const el = document.querySelector(id);
    if (!el) return;
    const open = force !== undefined ? force : el.getAttribute('aria-hidden') === 'true';
    el.setAttribute('aria-hidden', open ? 'false' : 'true');
    setDrawerChrome(open);
  }
  function closeAllDrawers() {
    document.querySelectorAll('[aria-hidden="false"][data-drawer]').forEach((el) => {
      el.setAttribute('aria-hidden', 'true');
    });
    setDrawerChrome(false);
  }
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-drawer-open]');
    if (t) { e.preventDefault(); toggleDrawer(t.getAttribute('data-drawer-open'), true); }
    const c = e.target.closest('[data-drawer-close]');
    if (c) { e.preventDefault(); toggleDrawer(c.getAttribute('data-drawer-close'), false); }
    if (e.target.matches('[data-overlay]')) closeAllDrawers();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAllDrawers();
  });

  /* Accordion --------------------------------------------- */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-accordion-trigger]');
    if (!trigger) return;
    const item = trigger.closest('.accordion__item');
    const panel = item.querySelector('.accordion__panel');
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';
    const scope = trigger.closest('[data-accordion-single]');
    if (scope) {
      scope.querySelectorAll('[data-accordion-trigger]').forEach((t) => {
        t.setAttribute('aria-expanded', 'false');
        const p = t.closest('.accordion__item').querySelector('.accordion__panel');
        if (p) p.setAttribute('data-open', 'false');
      });
    }
    trigger.setAttribute('aria-expanded', String(!isOpen));
    if (panel) panel.setAttribute('data-open', String(!isOpen));
  });

  /* Product image gallery + zoom + lightbox --------------- */
  let galleryVariantSyncLock = false;

  function parseGalleryMediaMap(gallery) {
    const el = gallery.querySelector('[data-gallery-media-map]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '{}');
    } catch {
      return null;
    }
  }

  function activateGalleryMedia(gallery, { mediaId, src, alt } = {}) {
    if (!gallery) return;
    const map = parseGalleryMediaMap(gallery);
    let targetSrc = src;
    let targetAlt = alt;
    if (mediaId != null && map?.items) {
      const item = map.items.find((m) => String(m.id) === String(mediaId));
      if (item) {
        targetSrc = item.src;
        targetAlt = item.alt;
      }
    }
    if (!targetSrc && mediaId != null) {
      const thumb = gallery.querySelector(`[data-gallery-thumb][data-media-id="${mediaId}"]`);
      if (thumb) targetSrc = thumb.getAttribute('data-src');
    }
    const main = gallery.querySelector('[data-gallery-main] img');
    if (main && targetSrc) {
      main.setAttribute('src', targetSrc);
      if (targetAlt) main.setAttribute('alt', targetAlt);
      if (mediaId != null) main.setAttribute('data-media-id', String(mediaId));
    }
    gallery.querySelectorAll('[data-gallery-thumb]').forEach((t) => {
      const idMatch = mediaId != null && t.getAttribute('data-media-id') === String(mediaId);
      const srcMatch = targetSrc && t.getAttribute('data-src') === targetSrc;
      t.setAttribute('aria-current', idMatch || srcMatch ? 'true' : 'false');
    });
  }

  function bindGalleryThumbs(gallery, handlers = {}) {
    const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    thumbs.forEach((thumb) => {
      if (thumb.dataset.bound) return;
      thumb.dataset.bound = 'true';
      thumb.addEventListener('click', () => {
        if (galleryVariantSyncLock) return;
        const src = thumb.getAttribute('data-src');
        const mediaId = thumb.getAttribute('data-media-id');
        activateGalleryMedia(gallery, { mediaId, src });
        if (mediaId && handlers.selectVariantForMediaId) {
          handlers.selectVariantForMediaId(mediaId);
        }
      });
    });
  }

  function bindGalleryTiles(gallery, handlers = {}) {
    gallery.querySelectorAll('[data-gallery-tile]').forEach((tile) => {
      if (tile.dataset.tileBound) return;
      tile.dataset.tileBound = 'true';
      const onActivate = () => {
        if (galleryVariantSyncLock) return;
        const src = tile.getAttribute('data-src');
        const mediaId = tile.getAttribute('data-media-id');
        activateGalleryMedia(gallery, { mediaId, src });
        if (mediaId && handlers.selectVariantForMediaId) {
          handlers.selectVariantForMediaId(mediaId);
        }
      };
      tile.addEventListener('click', onActivate);
      tile.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onActivate();
        }
      });
    });
  }

  function applyVariantGallery(gallery, media, handlers = {}) {
    if (!gallery || !media || !media.length) return;
    const mosaic = gallery.querySelector('[data-gallery-mosaic]');
    if (mosaic) {
      const tiles = [];
      for (let i = 0; i < 4; i += 1) {
        const m = media[i];
        if (m && m.full) {
          const alt = (m.alt || '').replace(/"/g, '&quot;');
          const mid = m.id != null ? ` data-media-id="${m.id}"` : '';
          tiles.push(
            `<div class="product-gallery__tile" data-gallery-tile data-src="${m.full}"${mid} role="button" tabindex="0"><img data-gallery-mosaic-img src="${m.w600 || m.full}" srcset="${m.w600 || m.full} 600w, ${m.full} 900w" sizes="(min-width: 990px) 25vw, 45vw" alt="${alt}" width="900" height="900" ${i === 0 ? 'fetchpriority="high"' : 'loading="lazy"'}></div>`
          );
        } else {
          tiles.push('<div class="product-gallery__tile product-gallery__tile--empty" data-gallery-mosaic-empty aria-hidden="true"></div>');
        }
      }
      mosaic.innerHTML = tiles.join('');
      bindGalleryTiles(gallery, handlers);
    }

    const mainBtn = gallery.querySelector('[data-gallery-main]');
    let main = gallery.querySelector('[data-gallery-main] img');
    if (mainBtn && media[0]?.full) {
      if (!main) {
        mainBtn.innerHTML = `<img src="${media[0].full}" alt="${(media[0].alt || '').replace(/"/g, '&quot;')}" width="1200" height="1200" fetchpriority="high">`;
        main = gallery.querySelector('[data-gallery-main] img');
      } else {
        main.setAttribute('src', media[0].full);
        if (media[0].alt) main.setAttribute('alt', media[0].alt);
      }
      if (media[0].id != null && main) main.setAttribute('data-media-id', String(media[0].id));
    }

    const thumbsWrap = gallery.querySelector('[data-gallery-thumbs]');
    if (thumbsWrap) {
      if (media.length <= 1) {
        thumbsWrap.hidden = true;
        thumbsWrap.innerHTML = '';
      } else {
        thumbsWrap.hidden = false;
        thumbsWrap.innerHTML = media
          .map(
            (m, i) => `
          <button type="button" class="product-gallery__thumb" data-gallery-thumb data-src="${m.full}"${m.id != null ? ` data-media-id="${m.id}"` : ''} aria-current="${i === 0 ? 'true' : 'false'}" aria-label="View image ${i + 1}">
            <img src="${m.thumb || m.full}" alt="" loading="lazy" width="200" height="200">
          </button>`
          )
          .join('');
        bindGalleryThumbs(gallery, handlers);
      }
    }
    bindMosaicZoom(gallery);
  }

  function bindMosaicZoom(gallery) {
    const isDesktopGallery = () => window.matchMedia('(min-width: 750px)').matches;
    gallery.querySelectorAll('.product-gallery__tile:not(.product-gallery__tile--empty)').forEach((tile) => {
      if (tile.dataset.zoomBound) return;
      tile.dataset.zoomBound = 'true';
      const img = tile.querySelector('img');
      if (!img) return;
      tile.addEventListener('mousemove', (e) => {
        if (!isDesktopGallery()) return;
        const rect = tile.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${x}% ${y}%`;
        img.style.transform = 'scale(1.12)';
      });
      tile.addEventListener('mouseleave', () => {
        img.style.transformOrigin = 'center center';
        img.style.transform = 'scale(1)';
      });
    });
  }

  function bindMobileGallerySwipe(gallery) {
    const mainBtn = gallery.querySelector('[data-gallery-main]');
    if (!mainBtn || mainBtn.dataset.swipeBound) return;
    mainBtn.dataset.swipeBound = 'true';

    const isMobileGallery = () => window.matchMedia('(max-width: 749px)').matches;
    let startX = 0;
    let startY = 0;
    let tracking = false;

    const step = (dir) => {
      const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
      if (thumbs.length < 2) return false;
      const current = thumbs.findIndex((t) => t.getAttribute('aria-current') === 'true');
      const idx = current < 0 ? 0 : current;
      const next = (idx + dir + thumbs.length) % thumbs.length;
      thumbs[next].click();
      return true;
    };

    mainBtn.addEventListener('touchstart', (e) => {
      if (!isMobileGallery()) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      tracking = true;
      mainBtn.dataset.gallerySwiped = 'false';
    }, { passive: true });

    mainBtn.addEventListener('touchend', (e) => {
      if (!isMobileGallery() || !tracking) return;
      tracking = false;
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
      if (step(dx > 0 ? -1 : 1)) {
        mainBtn.dataset.gallerySwiped = 'true';
      }
    }, { passive: true });
  }

  function initProductGalleryInteractions(gallery, handlers = {}) {
    if (!gallery || gallery.dataset.galleryInit) return;
    gallery.dataset.galleryInit = 'true';
    bindGalleryThumbs(gallery, handlers);
    bindGalleryTiles(gallery, handlers);
    bindMosaicZoom(gallery);
    bindMobileGallerySwipe(gallery);

    const mainBtn = gallery.querySelector('[data-gallery-main]');
    if (!mainBtn) return;

    const isMobileGallery = () => window.matchMedia('(max-width: 749px)').matches;

    mainBtn.addEventListener('click', () => {
      if (!isMobileGallery()) return;
      if (mainBtn.dataset.gallerySwiped === 'true') {
        mainBtn.dataset.gallerySwiped = 'false';
        return;
      }
      openLightbox(gallery);
    });
    mainBtn.addEventListener('mousemove', (e) => {
      if (!isMobileGallery()) return;
      const main = gallery.querySelector('[data-gallery-main] img');
      const rect = mainBtn.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      if (main) { main.style.transformOrigin = `${x}% ${y}%`; main.style.transform = 'scale(1.6)'; }
    });
    mainBtn.addEventListener('mouseleave', () => {
      if (!isMobileGallery()) return;
      const main = gallery.querySelector('[data-gallery-main] img');
      if (main) main.style.transform = 'scale(1)';
    });
  }

  function openLightbox(gallery) {
    let box = document.getElementById('LightboxRoot');
    if (!box) {
      box = document.createElement('div');
      box.id = 'LightboxRoot';
      box.style.cssText = 'position:fixed;inset:0;background:rgba(74,55,40,0.94);z-index:120;display:flex;align-items:center;justify-content:center;padding:24px';
      document.body.appendChild(box);
    }
    const images = [...gallery.querySelectorAll('[data-gallery-thumb]')].map((t) => t.getAttribute('data-src'));
    const src = gallery.querySelector('[data-gallery-main] img').getAttribute('src');
    let idx = Math.max(0, images.indexOf(src));
    box.innerHTML = `
      <button aria-label="Close" style="position:absolute;top:24px;right:24px;color:#F8F4EC;font-size:14px;letter-spacing:.2em;text-transform:uppercase" data-lb-close>Close</button>
      <button aria-label="Previous" data-lb-prev style="position:absolute;left:24px;top:50%;transform:translateY(-50%);color:#F8F4EC;font-size:32px">&larr;</button>
      <img src="${images[idx] || src}" style="max-width:90vw;max-height:88vh;object-fit:contain" alt="Product">
      <button aria-label="Next" data-lb-next style="position:absolute;right:24px;top:50%;transform:translateY(-50%);color:#F8F4EC;font-size:32px">&rarr;</button>`;
    document.body.style.overflow = 'hidden';
    const img = box.querySelector('img');
    const step = (dir) => { idx = (idx + dir + images.length) % images.length; img.src = images[idx]; };
    box.querySelector('[data-lb-close]').addEventListener('click', close);
    box.querySelector('[data-lb-prev]').addEventListener('click', () => step(-1));
    box.querySelector('[data-lb-next]').addEventListener('click', () => step(1));
    box.addEventListener('click', (e) => { if (e.target === box) close(); });
    // swipe
    let startX = 0;
    img.addEventListener('touchstart', (e) => startX = e.touches[0].clientX, { passive: true });
    img.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) step(dx > 0 ? -1 : 1);
    });
    function close() { box.remove(); document.body.style.overflow = ''; }
  }

  /* Variant picker ---------------------------------------- */
  function initProductVariantForms(root = document) {
    root.querySelectorAll('[data-product-form]').forEach((form) => {
      if (form.dataset.variantInit) return;
      form.dataset.variantInit = 'true';

      const dataEl = form.querySelector('[data-variant-json]');
      if (!dataEl) return;
      const variants = JSON.parse(dataEl.textContent || '[]');
      const swatches = form.querySelectorAll('[data-variant-option]');
      const priceEl = form.querySelector('[data-product-price]');
      const idInput = form.querySelector('[name="id"]');
      const stockEl = form.querySelector('[data-stock]');
      const notifyBtn = form.querySelector('[data-notify]');
      const atcBtn = form.querySelector('[data-atc]');
      const buyBtn = form.querySelector('[data-buy-now]');
      const stickyImg = document.querySelector('[data-sticky-image]');
      const stickyBuy = document.querySelector('[data-sticky-buy]');
      const gallery = form.closest('.product-layout')?.querySelector('[data-product-gallery]')
        || document.querySelector('[data-product-gallery]');

      const state = {};
      swatches.forEach((sw) => {
        const opt = sw.getAttribute('data-option-index');
        if (sw.getAttribute('aria-checked') === 'true') state[opt] = sw.getAttribute('data-value');
      });

      function applyVariantToSwatches(match) {
        if (!match) return;
        match.options.forEach((val, i) => {
          state[i] = val;
        });
        swatches.forEach((sw) => {
          const opt = sw.getAttribute('data-option-index');
          const val = sw.getAttribute('data-value');
          sw.setAttribute('aria-checked', state[opt] === val ? 'true' : 'false');
        });
      }

      function selectVariantById(variantId) {
        const match = variants.find((v) => String(v.id) === String(variantId));
        if (!match) return;
        if (idInput) idInput.value = match.id;
        applyVariantToSwatches(match);
        updateSelection();
      }

      function selectVariantForMediaId(mediaId) {
        const map = gallery ? parseGalleryMediaMap(gallery) : null;
        if (!map?.items) return;
        const item = map.items.find((m) => String(m.id) === String(mediaId));
        if (!item?.variantIds?.length) return;
        let targetId = item.variantIds[0];
        const featuredMatch = item.variantIds.find((vid) => {
          const v = variants.find((x) => String(x.id) === String(vid));
          return v && String(v.featured_media_id) === String(mediaId);
        });
        if (featuredMatch != null) targetId = featuredMatch;
        if (item.variantIds.length === 1) targetId = item.variantIds[0];
        selectVariantById(targetId);
      }

      const packSwitcher = form.querySelector('.product-pack-switcher');
      const galleryHandlers = packSwitcher
        ? {}
        : { selectVariantForMediaId };

      function syncGalleryForVariant(match) {
        if (!gallery) return;
        const isPackGallery = gallery.getAttribute('data-pack-gallery') === 'true';
        const galleryMedia = match.media && match.media.length ? match.media : null;
        const variantThumb = match.image || (galleryMedia && galleryMedia[0] && galleryMedia[0].full) || null;

        galleryVariantSyncLock = true;
        if (isPackGallery && galleryMedia) {
          applyVariantGallery(gallery, galleryMedia, galleryHandlers);
        } else if (match.featured_media_id != null) {
          activateGalleryMedia(gallery, { mediaId: match.featured_media_id });
        } else if (galleryMedia?.[0]) {
          activateGalleryMedia(gallery, {
            mediaId: galleryMedia[0].id,
            src: galleryMedia[0].full,
            alt: galleryMedia[0].alt,
          });
        } else if (variantThumb) {
          activateGalleryMedia(gallery, { src: variantThumb });
        }
        bindGalleryThumbs(gallery, galleryHandlers);
        bindGalleryTiles(gallery, galleryHandlers);
        bindMosaicZoom(gallery);
        galleryVariantSyncLock = false;
      }

      function updateSelection() {
        let match;
        const stateKeys = Object.keys(state);
        if (swatches.length === 0) {
          if (stateKeys.length > 0) {
            match = variants.find((v) => v.options.every((val, i) => state[i] != null && state[i] === val));
          }
          if (!match && idInput?.value) {
            match = variants.find((v) => String(v.id) === String(idInput.value));
          }
        } else {
          match = variants.find((v) => v.options.every((val, i) => state[i] != null && state[i] === val));
        }
        if (!match && idInput?.value) {
          match = variants.find((v) => String(v.id) === String(idInput.value));
        }
        if (!match) return;
        if (idInput) idInput.value = match.id;
        if (priceEl) priceEl.textContent = match.price;
        const mrpEls = document.querySelectorAll('[data-product-mrp]');
        mrpEls.forEach((el) => {
          if (match.compare_at) {
            el.hidden = false;
            el.textContent = match.compare_at;
          } else {
            el.hidden = true;
          }
        });
        document.querySelectorAll('[data-product-price]').forEach((el) => { el.textContent = match.price; });
        document.querySelectorAll('[data-product-off]').forEach((el) => {
          if (match.show_off && match.off_label) {
            el.hidden = false;
            el.textContent = match.off_label;
          } else if (match.off_pct > 0 && match.compare_at) {
            const suffix = el.getAttribute('data-off-suffix') || 'OFF';
            el.hidden = false;
            el.textContent = `${match.off_pct}% ${suffix}`;
          } else {
            el.hidden = true;
          }
        });
        const variantThumb = match.image || (match.media && match.media[0] && match.media[0].full) || null;
        if (stickyImg && variantThumb) stickyImg.setAttribute('src', variantThumb);
        syncGalleryForVariant(match);
        const available = match.available;
        if (stockEl) stockEl.textContent = available ? 'In stock' : '';
        if (atcBtn) {
          atcBtn.disabled = !available;
          atcBtn.textContent = atcBtn.dataset.labelIn || 'Add to cart';
        }
        if (buyBtn) {
          buyBtn.hidden = !available;
          buyBtn.disabled = !available;
        }
        if (stickyBuy) {
          stickyBuy.disabled = !available;
          stickyBuy.textContent = stickyBuy.dataset.labelIn || stickyBuy.textContent || 'Buy now';
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url);
        if (notifyBtn) notifyBtn.hidden = true;
        form.dispatchEvent(new CustomEvent('variant:change', { detail: match, bubbles: true }));
      }

      initProductGalleryInteractions(gallery, galleryHandlers);

      swatches.forEach((sw) => {
        sw.addEventListener('click', () => {
          const opt = sw.getAttribute('data-option-index');
          form.querySelectorAll(`[data-option-index="${opt}"]`).forEach((s) => s.setAttribute('aria-checked', 'false'));
          sw.setAttribute('aria-checked', 'true');
          state[opt] = sw.getAttribute('data-value');
          updateSelection();
        });
      });

      if (swatches.length === 0) {
        const urlVariant = new URL(window.location.href).searchParams.get('variant');
        const initialId = urlVariant || idInput?.value;
        if (initialId) selectVariantById(initialId);
        else updateSelection();
      } else {
        updateSelection();
      }

      form.querySelectorAll('.product-pack-switcher [data-pack-select]').forEach((pill) => {
        pill.addEventListener('click', (e) => {
          const variantId = pill.getAttribute('data-pack-select');
          if (!variantId) return;
          e.preventDefault();
          form.querySelectorAll('.product-pack-switcher .collection-pack-switcher__pill').forEach((p) => {
            const active = p === pill;
            p.classList.toggle('is-active', active);
            p.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          selectVariantById(variantId);
        });
      });
    });
  }

  initProductVariantForms();

  /* Collection pack switcher (instant, no full reload) -------- */
  function initCollectionPackSwitcher(scope) {
    const root = scope || document;
    root.querySelectorAll('[data-collection-pack-switcher]').forEach((toolbar) => {
      if (toolbar.dataset.packInit === 'true') return;
      toolbar.dataset.packInit = 'true';

      const key1 = toolbar.getAttribute('data-pack-key-1');
      const key2 = toolbar.getAttribute('data-pack-key-2');
      if (!key1 || !key2) return;

      const section = toolbar.closest('.collection-page') || toolbar.closest('.shopify-section') || document;
      const validKeys = new Set([key1, key2]);

      const readPackFromUrl = () => {
        const fromUrl = new URLSearchParams(window.location.search).get('pack');
        if (validKeys.has(fromUrl)) return fromUrl;
        return key1;
      };

      const applyPack = (packKey) => {
        if (!validKeys.has(packKey)) packKey = key1;

        section.querySelectorAll('[data-product-card]').forEach((card) => {
          const dataEl = card.querySelector('[data-product-card-packs]');
          if (!dataEl) return;
          let packs;
          try {
            packs = JSON.parse(dataEl.textContent || '{}');
          } catch {
            return;
          }
          const pack = packs[packKey];
          if (!pack) return;

          card.querySelectorAll('[data-product-card-link]').forEach((link) => {
            if (pack.url) link.setAttribute('href', pack.url);
          });

          const img = card.querySelector('[data-product-card-image]');
          if (img && pack.image) {
            img.src = pack.image;
            if (pack.image400) {
              img.srcset = `${pack.image400} 400w, ${pack.image} 800w`;
            }
            if (pack.alt) img.alt = pack.alt;
          }

          const hoverImg = card.querySelector('[data-product-card-hover]');
          if (hoverImg) {
            if (pack.hover) {
              hoverImg.src = pack.hover;
              hoverImg.hidden = false;
            } else if (pack.image) {
              hoverImg.src = pack.image;
            }
          }

          const priceEl = card.querySelector('[data-product-card-price]');
          if (priceEl && pack.priceHtml) priceEl.innerHTML = pack.priceHtml;

          card.querySelectorAll('[data-product-card-variant]').forEach((input) => {
            input.value = pack.variantId;
          });

          const wishBtn = card.querySelector('[data-wishlist-variant]');
          if (wishBtn && pack.variantId) wishBtn.setAttribute('data-wishlist-variant', pack.variantId);

          const soldBadge = card.querySelector('[data-product-card-sold-badge]');
          if (soldBadge) soldBadge.hidden = true;

          const atcBtn = card.querySelector('[data-product-card-atc]');
          if (atcBtn) {
            atcBtn.disabled = !pack.available;
            atcBtn.textContent = 'Add to cart';
          }

          const buyBtn = card.querySelector('[data-buy-now]');
          if (buyBtn) {
            buyBtn.disabled = !pack.available;
            buyBtn.hidden = !pack.available;
          }
        });

        toolbar.querySelectorAll('[data-pack-switch]').forEach((pill) => {
          const key = pill.getAttribute('data-pack-switch');
          const active = key === packKey;
          pill.classList.toggle('is-active', active);
          pill.setAttribute('aria-selected', active ? 'true' : 'false');
        });

        const url = new URL(window.location.href);
        url.searchParams.set('pack', packKey);
        window.history.replaceState({ pack: packKey }, '', url);
      };

      const onPackPillActivate = (pill) => {
        if (!pill || !toolbar.contains(pill)) return;
        const packKey = pill.getAttribute('data-pack-switch');
        if (!packKey) return;
        applyPack(packKey);
      };

      toolbar.addEventListener('click', (e) => {
        const pill = e.target.closest('[data-pack-switch]');
        if (!pill || !toolbar.contains(pill)) return;
        e.preventDefault();
        onPackPillActivate(pill);
      });

      applyPack(readPackFromUrl());
    });
  }

  initCollectionPackSwitcher();

  document.addEventListener('shopify:section:load', (e) => {
    initCollectionPackSwitcher(e.target);
    e.target.querySelectorAll('[data-product-form]').forEach((f) => {
      delete f.dataset.variantInit;
    });
    e.target.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      delete gallery.dataset.galleryInit;
    });
    initProductVariantForms(e.target);
  });

  /* Quantity steppers ------------------------------------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    const wrap = btn.closest('[data-qty]');
    const input = wrap.querySelector('input');
    const step = parseInt(btn.getAttribute('data-qty-step'), 10);
    const min = parseInt(input.getAttribute('min') || '1', 10);
    const next = Math.max(min, (parseInt(input.value, 10) || min) + step);
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* Sticky Buy now ----------------------------------------- */
  const productForm = document.querySelector('[data-product-form]');
  const stickyAtc = document.querySelector('[data-sticky-atc]');
  if (productForm && stickyAtc) {
    const buyBtn = productForm.querySelector('[data-buy-now]') || productForm.querySelector('[data-atc]');
    const updateSticky = () => {
      const target = buyBtn || productForm;
      const past = target.getBoundingClientRect().bottom < 88;
      stickyAtc.setAttribute('data-visible', past ? 'true' : 'false');
    };
    updateSticky();
    window.addEventListener('scroll', updateSticky, { passive: true });
    window.addEventListener('resize', updateSticky);
  }

  /* Video bubble ------------------------------------------ */
  const VIDEO_DISMISS_KEY = 'shanium_video_bubble_dismissed';
  const videoBubble = document.querySelector('[data-video-bubble]');
  if (videoBubble && sessionStorage.getItem(VIDEO_DISMISS_KEY) === '1') {
    videoBubble.hidden = true;
  }

  const ensureVideoSrc = (video) => {
    if (!video || video.src) return;
    const src = video.getAttribute('data-video-src');
    if (src) video.src = src;
  };

  const playMiniBubbleVideo = () => {
    if (!videoBubble || videoBubble.hidden) return;
    const mini = videoBubble.querySelector('[data-video-mini]');
    const openBtn = videoBubble.querySelector('[data-video-open]');
    if (!mini) return;
    ensureVideoSrc(mini);
    mini.muted = true;
    mini.loop = true;
    mini.playsInline = true;
    const onReady = () => {
      mini.play?.().catch(() => {});
      openBtn?.classList.add('is-playing');
    };
    if (mini.readyState >= 2) onReady();
    else mini.addEventListener('loadeddata', onReady, { once: true });
  };

  const bootMiniBubble = () => {
    if (!videoBubble || videoBubble.hidden) return;
    playMiniBubbleVideo();
  };

  if (videoBubble && !videoBubble.hidden) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(bootMiniBubble, { timeout: 800 });
    } else {
      window.addEventListener('load', bootMiniBubble, { once: true });
    }
  }

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) bootMiniBubble();
  });

  document.addEventListener('click', (e) => {
    const dismissBtn = e.target.closest('[data-video-dismiss]');
    if (dismissBtn) {
      e.preventDefault();
      e.stopPropagation();
      const wrap = dismissBtn.closest('[data-video-bubble]');
      if (wrap) {
        wrap.hidden = true;
        const mini = wrap.querySelector('[data-video-mini]');
        if (mini) mini.pause?.();
      }
      try { sessionStorage.setItem(VIDEO_DISMISS_KEY, '1'); } catch (_) {}
      return;
    }

    if (e.target.closest('[data-video-open]')) {
      const pop = document.querySelector('[data-video-popup]');
      if (pop) {
        pop.setAttribute('data-open', 'true');
        document.body.style.overflow = 'hidden';
        const mini = videoBubble?.querySelector('[data-video-mini]');
        if (mini) mini.pause?.();
        const v = pop.querySelector('[data-video-expanded]');
        if (v) {
          ensureVideoSrc(v);
          v.muted = false;
          v.loop = true;
          v.currentTime = 0;
          v.play?.().catch(() => {});
        }
      }
    }

    if (e.target.closest('[data-video-close]') || e.target.matches('[data-video-popup]')) {
      const pop = document.querySelector('[data-video-popup]');
      if (pop) {
        pop.setAttribute('data-open', 'false');
        document.body.style.overflow = '';
        const v = pop.querySelector('[data-video-expanded]');
        if (v) {
          v.pause?.();
          v.muted = true;
        }
        playMiniBubbleVideo();
        const iframe = pop.querySelector('iframe');
        if (iframe) {
          const src = iframe.src;
          iframe.src = src;
        }
      }
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const pop = document.querySelector('[data-video-popup][data-open="true"]');
    if (!pop) return;
    pop.setAttribute('data-open', 'false');
    document.body.style.overflow = '';
    const v = pop.querySelector('[data-video-expanded]');
    if (v) {
      v.pause?.();
      v.muted = true;
    }
    playMiniBubbleVideo();
  });

  /* Estimated delivery date ------------------------------- */
  document.querySelectorAll('[data-eta-days]').forEach((el) => {
    const days = parseInt(el.getAttribute('data-eta-days'), 10) || 3;
    const d = new Date(); d.setDate(d.getDate() + days);
    el.textContent = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  });

  /* Contact / enquiry / newsletter → Shopify /contact ----- */
  const newsletterCopy = window.theme?.strings?.newsletter || {};
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function normalizePhone(value) {
    return String(value || '').replace(/[\s().-]/g, '');
  }

  function isValidPhone(value) {
    const digits = normalizePhone(value).replace(/\D/g, '');
    return digits.length >= 8 && digits.length <= 15;
  }

  function prepareFooterNewsletter(form) {
    const contactInput = form.querySelector('[data-footer-newsletter-contact]');
    const emailHidden = form.querySelector('[data-footer-newsletter-email]');
    const phoneHidden = form.querySelector('[data-footer-newsletter-phone]');
    const bodyHidden = form.querySelector('[data-footer-newsletter-body]');
    const clientError = form.querySelector('[data-newsletter-client-error]');
    const showFooterError = (message) => {
      if (!clientError) return;
      clientError.textContent = message;
      clientError.hidden = !message;
    };
    const value = contactInput?.value.trim() || '';
    if (!value) {
      showFooterError(newsletterCopy.error_email || 'Please enter a valid email or phone number.');
      contactInput?.focus();
      return false;
    }
    if (emailPattern.test(value)) {
      if (emailHidden) emailHidden.value = value;
      if (phoneHidden) phoneHidden.value = '';
      showFooterError('');
      return true;
    }
    if (isValidPhone(value)) {
      const digits = normalizePhone(value).replace(/\D/g, '');
      if (emailHidden) emailHidden.value = `newsletter+${digits}@customers.shaniumyum.com`;
      if (phoneHidden) phoneHidden.value = value;
      if (bodyHidden) bodyHidden.value = `Newsletter signup from footer (phone: ${value})`;
      showFooterError('');
      return true;
    }
    showFooterError(newsletterCopy.error_phone || 'Please enter a valid email or phone number.');
    contactInput?.focus();
    return false;
  }

  function validateNewsletterForm(form) {
    const emailInput = form.querySelector('[type="email"][name="contact[email]"]');
    const phoneInput = form.querySelector('[data-newsletter-phone]');
    const consentInput = form.querySelector('[data-newsletter-consent]');
    const clientError = form.querySelector('[data-newsletter-client-error]');
    const showClientError = (message) => {
      if (!clientError) return;
      clientError.textContent = message;
      clientError.hidden = !message;
    };
    if (!emailInput) return true;
    const email = emailInput.value.trim();
    if (!email || !emailPattern.test(email)) {
      showClientError(newsletterCopy.error_email || 'Please enter a valid email address.');
      emailInput.setAttribute('aria-invalid', 'true');
      emailInput.focus();
      return false;
    }
    emailInput.setAttribute('aria-invalid', 'false');
    if (phoneInput && (phoneInput.required || phoneInput.value.trim())) {
      if (!isValidPhone(phoneInput.value)) {
        showClientError(newsletterCopy.error_phone || 'Please enter a valid phone number.');
        phoneInput.setAttribute('aria-invalid', 'true');
        phoneInput.focus();
        return false;
      }
      phoneInput.setAttribute('aria-invalid', 'false');
    }
    if (consentInput && consentInput.required && !consentInput.checked) {
      showClientError(newsletterCopy.error_consent || 'Please agree to receive marketing emails to subscribe.');
      consentInput.focus();
      return false;
    }
    showClientError('');
    return true;
  }

  async function postContactForm(form) {
    const res = await fetch('/contact', {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'text/html' },
    });
    return res.ok || res.redirected;
  }

  function showContactSuccess(form) {
    const success = form.querySelector('[data-form-success]');
    const fieldWrap = form.querySelector('[data-contact-form-fields]');
    if (fieldWrap) fieldWrap.hidden = true;
    form.querySelectorAll('label, button[type="submit"], input:not([type="hidden"]), textarea, select').forEach((el) => {
      if (el.closest('[data-form-success]')) return;
      el.hidden = true;
    });
    if (success) {
      success.hidden = false;
      success.focus?.();
    }
  }

  document.querySelectorAll('form[data-contact-form]').forEach((form) => {
    const emailInput = form.querySelector('[type="email"][name="contact[email]"]');
    const phoneInput = form.querySelector('[data-newsletter-phone]');
    const consentInput = form.querySelector('[data-newsletter-consent]');
    const clientError = form.querySelector('[data-newsletter-client-error]');
    [emailInput, phoneInput, consentInput].forEach((el) => {
      if (!el || !clientError) return;
      el.addEventListener('input', () => {
        clientError.hidden = true;
        clientError.textContent = '';
      });
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (form.matches('[data-footer-newsletter-form]') && !prepareFooterNewsletter(form)) return;
      if (form.matches('[data-newsletter-form]') && !validateNewsletterForm(form)) return;

      const btn = form.querySelector('[type="submit"]');
      const btnLabel = btn?.textContent;
      if (btn) {
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        btn.textContent = 'Sending…';
      }
      try {
        const ok = await postContactForm(form);
        if (!ok) {
          form.submit();
          return;
        }
        showContactSuccess(form);
      } catch (_) {
        form.submit();
      } finally {
        if (btn && btnLabel && form.querySelector('[data-form-success]')?.hidden !== false) {
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
          btn.textContent = btnLabel;
        }
      }
    });
  });

  /* FBT bundle -------------------------------------------- */
  document.querySelectorAll('[data-fbt]').forEach((fbt) => {
    const addAll = fbt.querySelector('[data-fbt-add]');
    if (!addAll) return;
    addAll.addEventListener('click', async () => {
      const ids = [...fbt.querySelectorAll('[data-fbt-variant]:checked')].map((i) => ({
        id: parseInt(i.value, 10), quantity: 1
      }));
      if (!ids.length) return;
      addAll.disabled = true; addAll.textContent = 'Adding…';
      try {
        await fetch(window.theme.routes.cart_add_url, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: ids })
        });
        window.dispatchEvent(new CustomEvent('cart:refresh'));
        window.dispatchEvent(new CustomEvent('cart:open'));
      } finally {
        addAll.disabled = false; addAll.textContent = addAll.dataset.label || 'Add all to cart';
      }
    });
  });

  /* Product tabs ------------------------------------------ */
  document.querySelectorAll('[data-product-tabs]').forEach((wrap) => {
    const tabs = [...wrap.querySelectorAll('[data-tab]')];
    const panels = [...wrap.querySelectorAll('[data-panel]')];
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const id = tab.getAttribute('data-tab');
        tabs.forEach((t) => t.setAttribute('aria-selected', t === tab ? 'true' : 'false'));
        panels.forEach((panel) => {
          const match = panel.getAttribute('data-panel') === id;
          panel.hidden = !match;
          panel.setAttribute('data-open', match ? 'true' : 'false');
        });
      });
    });
  });

  document.querySelectorAll('[data-read-more]').forEach((wrap) => {
    const btn = wrap.querySelector('[data-read-more-toggle]');
    if (!btn) return;
    btn.addEventListener('click', () => {
      const expanded = wrap.getAttribute('data-expanded') === 'true';
      wrap.setAttribute('data-expanded', expanded ? 'false' : 'true');
      btn.textContent = expanded
        ? (btn.dataset.labelMore || 'Read more')
        : (btn.dataset.labelLess || 'Show less');
    });
  });

  /* Product panels (bulk drawer + ask modal) -------------- */
  const openProductPanel = (name) => {
    const panel = document.querySelector(`[data-product-panel="${name}"]`);
    if (!panel) return;
    panel.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };
  const closeProductPanels = () => {
    document.querySelectorAll('[data-product-panel]').forEach((p) => p.setAttribute('aria-hidden', 'true'));
    document.body.style.overflow = '';
  };
  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-product-panel-open]');
    if (openBtn) {
      e.preventDefault();
      closeAllDrawers();
      openProductPanel(openBtn.getAttribute('data-product-panel-open'));
      return;
    }
    if (e.target.closest('[data-product-panel-close]')) {
      e.preventDefault();
      closeProductPanels();
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeProductPanels();
  });

  /* Share + Buy now --------------------------------------- */
  document.addEventListener('click', async (e) => {
    const shareBtn = e.target.closest('[data-product-share]');
    if (shareBtn) {
      e.preventDefault();
      const shareData = { title: document.title, url: window.location.href };
      try {
        if (navigator.share) await navigator.share(shareData);
        else {
          await navigator.clipboard.writeText(window.location.href);
          const label = shareBtn.querySelector('span');
          if (label) {
            const prev = label.textContent;
            label.textContent = 'Link copied';
            setTimeout(() => { label.textContent = prev; }, 1600);
          }
        }
      } catch (_) {}
      return;
    }

    const buyNow = e.target.closest('[data-buy-now]');
    if (!buyNow) return;
    e.preventDefault();
    const form = buyNow.closest('form') || document.querySelector('[data-product-form]');
    if (!form) return;
    const idInput = form.querySelector('[name="id"]');
    const qtyInput = form.querySelector('[name="quantity"]');
    const id = idInput?.value;
    const quantity = parseInt(qtyInput?.value || '1', 10) || 1;
    if (!id) return;
    buyNow.disabled = true;
    try {
      await fetch((window.theme?.routes?.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(id, 10), quantity }] })
      });
      window.location.href = '/checkout';
    } catch (_) {
      buyNow.disabled = false;
    }
  });
})();
